import type { CopilotScope } from "@productlane/lib/src/ai"
import type { AiChatSession } from "@productlane/zero/schema"

import type { AgentItem } from "./agent-items"
import { proposalRowTime } from "./agent-items"
import { agentPath, expandedChatPath } from "./agent-route"
import { ideaTitle } from "./idea-list"

export type AssistantChat = Pick<
	AiChatSession,
	"id" | "scopeKind" | "scopeId" | "title" | "lastMessageAt" | "createdAt"
>

/** The scope a stored chat reopens with, or null for one it cannot place. */
export function scopeForChat(chat: AssistantChat): CopilotScope | null {
	const id = chat.scopeId ?? null
	switch (chat.scopeKind) {
		case "thread":
			return id ? { kind: "thread", threadId: id } : null
		case "article":
			return id ? { kind: "article", articleId: id } : null
		case "changelog":
			return id ? { kind: "changelog", changelogId: id } : null
		case "workspace":
			return { kind: "workspace" }
		case "reporting":
			return { kind: "reporting" }
		case "agent":
			return id ? { kind: "agent", chatId: id } : { kind: "agent" }
		case "agent-idea":
			return id ? { kind: "agent", recommendationId: id } : null
		default:
			return null
	}
}

export function chatPath(chat: AssistantChat): string | null {
	const scope = scopeForChat(chat)
	if (!scope) return null
	return `${expandedChatPath(scope)}&session=${encodeURIComponent(chat.id)}`
}

export type AssistantSurface =
	| "ideas"
	| "inbox"
	| "docs"
	| "changelog"
	| "other"

export function chatSurface(
	scopeKind: string | null | undefined,
): AssistantSurface {
	switch (scopeKind) {
		case "thread":
		case "workspace":
			return "inbox"
		case "article":
			return "docs"
		case "changelog":
			return "changelog"
		default:
			return "other"
	}
}

export const SURFACE_LABELS: Record<AssistantSurface, string> = {
	ideas: "Idea",
	inbox: "Inbox",
	docs: "Docs",
	changelog: "Changelog",
	other: "Agent",
}

export interface AgentSidebarRow {
	id: string
	title: string
	href: string
	at: number
	// Set for a stored chat, so its row can be archived.
	chatId?: string
	surface: AssistantSurface
}

/** Ideas and the user's chats in one list, newest first, like Claude's recents. */
export function agentSidebarRows(
	ideas: readonly AgentItem[],
	chats: readonly AssistantChat[],
): AgentSidebarRow[] {
	const ideaIds = new Set(ideas.map((item) => item.id))
	const ideaRows = ideas.map((item): AgentSidebarRow => ({
		id: item.id,
		title: ideaTitle(item),
		href: agentPath("all", item.id),
		at: ideaTime(item),
		surface: "ideas",
	}))
	const chatRows = chats.flatMap((chat): AgentSidebarRow[] => {
		// An idea's own chat already sits behind the idea's row.
		if (
			(chat.scopeKind === "agent-idea" || chat.scopeKind === "agent") &&
			chat.scopeId &&
			ideaIds.has(chat.scopeId)
		) {
			return []
		}
		const href = chatPath(chat)
		if (!href) return []
		return [
			{
				id: chat.id,
				title: chat.title?.trim() || "New chat",
				href,
				at: chat.lastMessageAt ?? chat.createdAt,
				chatId: chat.id,
				surface: chatSurface(chat.scopeKind),
			},
		]
	})
	return [...ideaRows, ...chatRows].sort((a, b) => b.at - a.at)
}

/** The Assistant section: chats only. Ideas live under Recommendations, and so does an open idea's chat. */
export function assistantSidebarRows(
	ideas: readonly AgentItem[],
	chats: readonly AssistantChat[],
): AgentSidebarRow[] {
	return agentSidebarRows(ideas, chats).filter((row) => row.chatId)
}

function ideaTime(item: AgentItem): number {
	switch (item.kind) {
		case "setup":
			return 0
		case "recommendation":
			return item.recommendation.createdAt
		case "code":
			return item.fix.statusChangedAt
		case "docs":
			return item.group.createdAt
		case "proposal":
			return proposalRowTime(item.row)
	}
}

export type ChatsTab = "all" | AssistantSurface | "archived"

export const CHATS_TABS: { id: ChatsTab; label: string }[] = [
	{ id: "all", label: "All" },
	{ id: "ideas", label: "Ideas" },
	{ id: "inbox", label: "Inbox" },
	{ id: "docs", label: "Docs" },
	{ id: "changelog", label: "Changelog" },
	{ id: "archived", label: "Archived" },
]

export function rowsForTab(
	rows: readonly AgentSidebarRow[],
	tab: ChatsTab,
): AgentSidebarRow[] {
	if (tab === "all" || tab === "archived") return [...rows]
	return rows.filter((row) => row.surface === tab)
}

// View all grows a page at a time; past the ceiling a chat is a search away.
export const CHATS_PAGE_SIZE = 200
export const CHATS_PAGE_MAX = 1000

/** The next limit for View all, or null when the last page came back short. */
export function nextChatsLimit(loaded: number, limit: number): number | null {
	if (loaded < limit || limit >= CHATS_PAGE_MAX) return null
	return Math.min(limit + CHATS_PAGE_SIZE, CHATS_PAGE_MAX)
}
